import { useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import {
  createSiteTerm,
  exactTerm,
  loadSiteTerms,
  matchTerms,
  toTermValue,
  vocabularyOf,
} from './termRefStore'

/**
 * Champ de formulaire du type `term-ref` — une catégorie en SAISIE LIBRE.
 *
 * Valeur : `{tid, vocabulary, name}` (ou null).
 *
 * C'est un champ, pas une pop-in : on tape, on choisit dans la liste, ou on
 * valide une saisie neuve — et c'est le « Enregistrer » du formulaire qui
 * décide, comme pour le titre juste au-dessus. Il n'y a donc ni bouton
 * « Appliquer », ni « Retirer la référence », ni identifiant technique à
 * l'écran : le rédacteur voit le NOM de sa catégorie, rien d'autre.
 *
 * Les règles de fond (liste bornée au site, création dans ce site) vivent dans
 * `termRefStore` — partagées avec l'édition EN PLACE du canevas, qui est la
 * surface par défaut : dans le rendu, une catégorie se modifie là où elle
 * s'affiche (cf. `activateInlineTerm` dans `BuilderBlockWrapper`).
 */
export const TermRefCell = ({ id, value, onChange, vocabularies, siteId = null, placeholder }) => {
  const vocabulary = vocabularyOf(vocabularies)

  const [terms, setTerms] = useState([])
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const boxRef = useRef(null)

  // Le vocabulaire d'un site tient en quelques dizaines de termes : on le
  // charge UNE fois et on filtre en mémoire. Une recherche par requête à
  // chaque frappe ferait clignoter la liste pour rien.
  useEffect(() => {
    let cancelled = false
    loadSiteTerms(vocabulary, siteId).then((rows) => { if (!cancelled) setTerms(rows) })
    return () => { cancelled = true }
  }, [vocabulary, siteId])

  // Refermer la liste au clic dehors — sans quoi elle resterait ouverte
  // par-dessus les champs suivants.
  useEffect(() => {
    if (!open) return
    const onDown = (e) => { if (!boxRef.current?.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const typed = query.trim()
  const matches = matchTerms(terms, typed)
  const exact = exactTerm(terms, typed)

  const pick = (term) => {
    onChange(toTermValue(term, vocabulary))
    setQuery('')
    setOpen(false)
    setError(null)
  }

  const create = async () => {
    if (!typed || !vocabulary || busy) return
    setBusy(true)
    setError(null)
    try {
      const term = await createSiteTerm(vocabulary, typed, siteId)
      setTerms((prev) => [...prev, term])
      pick(term)
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (exact) pick(exact)
      else if (typed) create()
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  if (!vocabulary) {
    return <p className="text-xs text-red-600">Aucun vocabulaire déclaré sur ce champ.</p>
  }

  // Une catégorie choisie s'affiche comme une pastille : c'est UNE valeur, pas
  // une saisie en cours. La retirer rend le champ de saisie.
  if (value?.tid) {
    return (
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-teal-50 border border-teal-200 px-3 py-1 text-sm text-teal-900">
          {value.name}
          <button
            type="button"
            onClick={() => onChange(null)}
            className="rounded-full p-0.5 hover:bg-teal-100"
            aria-label={`Retirer la catégorie ${value.name}`}
          >
            <X size={13} aria-hidden="true" />
          </button>
        </span>
      </div>
    )
  }

  return (
    <div ref={boxRef} className="relative">
      <input
        id={id}
        type="text"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder || 'Saisir ou choisir une catégorie…'}
        className="input"
        autoComplete="off"
      />

      {open && (
        <ul className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
          {matches.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                onClick={() => pick(t)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
              >
                {t.name}
              </button>
            </li>
          ))}
          {/* Créer reste offert même quand des noms approchants remontent
              (« Drupal » à côté de « Drupal 11 ») — mais jamais sur un nom
              déjà pris, que le serveur refuserait. */}
          {typed && !exact && (
            <li>
              <button
                type="button"
                onClick={create}
                disabled={busy}
                className="w-full text-left px-3 py-2 text-sm text-teal-800 hover:bg-teal-50 disabled:opacity-50"
              >
                {busy ? 'Création…' : <>Créer «&nbsp;{typed}&nbsp;»</>}
              </button>
            </li>
          )}
          {matches.length === 0 && !typed && (
            <li className="px-3 py-2 text-xs text-gray-400">
              Aucune catégorie sur ce site — saisissez-en une.
            </li>
          )}
        </ul>
      )}

      {error && <p role="alert" className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  )
}
